/**
 * Every react-query key in the app, in one module.
 *
 * Keys are read by more than the hooks that own them — the route guards ask for the
 * session and the capability probe, the 401 handler invalidates the session, mutations
 * invalidate the user list — so they cannot live next to one of those readers without
 * creating an import cycle.
 */
export const queryKeys = {
  /** `{ session, user } | null`: whether anyone is signed in. */
  session: ['auth', 'session'] as const,
  /** Native sessions belonging to this user, across browsers/devices. */
  ownSessions: (userId?: string) =>
    userId === undefined
      ? (['auth', 'own-sessions'] as const)
      : (['auth', 'own-sessions', userId] as const),
  /** Menu capability only; does not fetch or populate the user list. */
  capability: ['auth', 'capability'] as const,
  /**
   * `{ google, github }`: which social providers this deployment configured.
   *
   * It is a read of the API's own configuration (`GET /auth/providers`), not of the
   * session, but the login page is the only reader and it must not fail because of it.
   */
  authProviders: ['auth', 'providers'] as const,
  /** Required public CAPTCHA widget settings; errors must not disable protection. */
  authCaptcha: ['auth', 'captcha'] as const,
  /**
   * 这个浏览器保存的账号会话（Better Auth multi-session 的
   * `GET /api/auth/multi-session/list-device-sessions`）。
   *
   * 不放在 `session` 那条键下面：它回答的是"这台机器上还有谁登录着"，和"现在是谁"
   * 是两个问题，撤销一个账号的会话也不该让当前会话的缓存失效。
   */
  deviceSessions: ['auth', 'device-sessions'] as const,
  /** The business user list. */
  users: ['users'] as const,
  /** Page parameters are part of the key; writes invalidate the shared users prefix. */
  usersPage: (query: Readonly<Record<string, string | number | undefined>>) =>
    ['users', 'list', query] as const,
  userStats: ['users', 'stats'] as const,
  ownUser: ['users', 'me'] as const,
  /** One business user with its photos. */
  user: (userId: string) => ['users', userId] as const,
  /**
   * One business user's login sessions (`GET /users/:id/sessions`), admin only.
   *
   * 挂在 `users` 前缀下，是因为它是同一屏的问题：用户被改、被停用时，这些会话也必须重新问
   * 一遍，而现有的一次 `invalidateQueries({queryKey: queryKeys.users})` 已经覆盖了它。
   */
  userSessions: (userId: string) => ['users', userId, 'sessions'] as const,
  /**
   * The dictionary key space: `['dicts']` is the whole of it.
   *
   * Every dictionary write invalidates this one prefix. The three things under it — the type
   * list, one type's detail, one code's options — are not independent (an item write changes
   * both the detail and the lookup answer), so listing affected keys by hand is how a screen
   * ends up showing the answer to a question that has already changed. The data is small
   * enough that re-asking is cheaper than being wrong.
   */
  dicts: ['dicts'] as const,
  /** One dictionary type with its items (`GET /dicts/:id`). */
  dict: (dictTypeId: string) => ['dicts', dictTypeId] as const,
  /** The options one code resolves to (`GET /dicts/code/:type`) — the API's own answer, cached. */
  dictCode: (type: string) => ['dicts', 'code', type] as const,
  /**
   * Every configuration key (`GET /configs`).
   *
   * One key for the whole page: the answer is the catalogue plus what the table and the process
   * say about each entry, and a save returns the same shape — which is why a successful write
   * replaces this entry instead of invalidating it.
   */
  configs: ['configs'] as const,
  configGroups: ['configs', 'groups'] as const,
  /** 本地应用、访问与审计日志查询的统一前缀。 */
  logs: ['logs'] as const,
  /** Menu management lists, parent candidates and editor catalogue. */
  menus: ['menus'] as const,
  tasks: ['tasks'] as const,
  /** 完整部门树，列表与上级选择共享同一份数据。 */
  departments: ['departments'] as const,
  userDepartmentOptions: ['users', 'department-options'] as const,
  /** Role/membership changes already invalidate the auth/access prefix. */
  menuNavigation: ['auth', 'access', 'menus'] as const,
  /**
   * Whether this deployment lets the page restart the API (`GET /configs/restart`).
   *
   * Under the `configs` prefix rather than a key of its own: it is the same screen's question, and
   * a restart invalidates the list anyway.
   */
  configRestart: ['configs', 'restart'] as const,
  /** The API's own liveness probe. */
  health: ['health'] as const,
  /** Public metrics from the application's Redis connection; administrator only. */
  redisMonitor: ['monitor', 'redis'] as const,
  /** Host and current API process metrics; administrator only. */
  serviceMonitor: ['monitor', 'service'] as const,
  /** 当前可阅读的协议与隐私文书。 */
  legalDocuments: ['legal', 'documents'] as const,
  legalDocument: (documentKey: string) =>
    ['legal', 'documents', documentKey] as const,
  /** 当前登录身份的文书确认记录。 */
  legalAcceptances: ['legal', 'acceptances'] as const,
};

/**
 * Whether a cache entry is the session.
 *
 * The 401 handler must not treat the session query's own failure as "the session just
 * expired" — that is the ordinary "nobody is signed in" answer — and it must not skip a
 * 401 from anything else. Comparing both segments is what keeps those apart.
 */
export const queryKeyTargetsSession = (key: readonly unknown[]): boolean =>
  key[0] === queryKeys.session[0] && key[1] === queryKeys.session[1];
